import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

export default function Gallery({ items = [], heading = true, preview = false }) {
  const [active, setActive] = useState(null);
  const list = preview ? items.slice(0, 6) : items;

  return (
    <section className="bg-secondary py-24 md:py-32" data-testid="gallery-section">
      <div className="rb-container">
        {heading && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal>
              <p className="overline text-accent-foreground/60 mb-4 flex items-center gap-3">
                <span className="h-px w-10 bg-[var(--rb-gold)] inline-block" /> Moments on Stage
              </p>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.02]">
                Where light, grace &amp; rhythm meet.
              </h2>
            </Reveal>
            {preview && (
              <Reveal delay={0.1}>
                <Link to="/gallery" data-testid="gallery-view-all" className="inline-flex items-center gap-2 text-foreground font-medium border-b-2 border-[var(--rb-gold)] pb-1 hover:gap-4 transition-all duration-300">
                  View full gallery
                </Link>
              </Reveal>
            )}
          </div>
        )}

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {list.map((g, i) => (
            <motion.button
              key={g.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              onClick={() => setActive(g)}
              data-testid={`gallery-item-${i}`}
              className="group relative mb-5 block w-full img-zoom overflow-hidden break-inside-avoid"
            >
              <img src={g.image} alt={g.title || "Dance performance"} className="w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-[#1A0F2E]/0 group-hover:bg-[#1A0F2E]/55 transition-colors duration-400 flex items-center justify-center">
                <span className="grid h-14 w-14 place-items-center border border-[var(--rb-gold)] text-[var(--rb-gold)] opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-400">
                  <Plus className="h-6 w-6" />
                </span>
              </div>
              {g.title && (
                <span className="absolute bottom-3 left-3 font-serif text-lg text-[#F6F4EE] opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  {g.title}
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#1A0F2E]/95 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-[#F6F4EE] hover:text-gold" data-testid="lightbox-close" aria-label="Close">
              <X className="h-8 w-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={active.image}
              alt={active.title || "Dance performance"}
              className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
